import type { JobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { type ConversationViewModel } from '@/features/messages/ConversationViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import type { ExternalReferenceContent } from '@/features/messages/content'
import { ChatListViewModel } from '@/features/messages/ChatListViewModel'
import { type ConversationMessageViewModel } from '@/features/messages/ConversationMessageViewModel'
import type { JobTaxAssistantStore } from '@/features/jobs/taxes/assistant/JobTaxAssistantStore'
import { task } from 'mobx-task'
import { ignoreAbortError } from '@/utils/abort-util'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import type { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import { TaxJobType } from '@/features/jobs/job-type'
import { computeProgressionFlow } from '@/features/jobs/taxes/progression/progression-content'

/**
 * A tax-job-specific implementation of {@link JobConversationViewModel}
 * with special handling for messages pertaining to job progressions and
 * the tax assistant.
 */
export class TaxJobConversationViewModel implements JobConversationViewModel {
  private readonly _lifecycle = new LifecycleTracker()

  readonly chatVm

  /**
   * The job progression instance, if any.
   */
  @observable accessor progression: JobProgression | null = null

  constructor(
    private readonly _taxAssistantStore: JobTaxAssistantStore,
    private readonly _progressionStore: ProgressionStore,
    private readonly _viewLatestDraft: () => unknown,
    public readonly job: SubmittedJob,
    public readonly conversationVm: ConversationViewModel,
  ) {
    this.chatVm = new ChatListViewModel<
      ConversationMessageViewModel,
      JobMessageViewModel,
      TaxJobConversationEvent
    >({
      messages: () => this.conversationVm.messages.slice(),
      events: () => this.conversationEvents,
      createMessageVm: (messageVm) =>
        this._createJobMessageViewModel(messageVm),
      getEventDate: (event) => event.date,
      getMessageSender: (vm) => vm.message.sender,
      getMessageDate: (vm) => vm.message.dateCreated,
      activateMessage: (vm) => vm.activate(),
      deactivateMessage: (vm) => vm.deactivate(),
      maxDurationBetweenSameSenderMessages: 3 * 60 * 1000, // 3 minutes
    })
    this._lifecycle.manage(this.chatVm)

    // The job-specific conversation VM manages the underlying
    // actual conversation vm.
    this._lifecycle.manage(this.conversationVm)
  }

  /**
   * The number of unread messages in the conversation.
   */
  @computed get unreadCount() {
    return this.conversationVm.unreadCount
  }

  /**
   * The tax assistant instance, if any.
   */
  @computed get taxAssistant() {
    // Tax assistant is created in the background after submission
    // and will be populated by real-time events, so this will
    // ensure that we'll pick up the real-time update for the assistant.
    return this._taxAssistantStore.getTaxAssistant(this.job.id)
  }

  /** @inheritDoc */
  @computed get isAvailableInInfoGathering() {
    return (
      Boolean(this.taxAssistant) ||
      this.job.workspace.config.jobs.enableChatInInfoGathering
    )
  }

  /**
   * Whether the assistant is "typing".
   */
  @computed get isAssistantTyping() {
    return this.taxAssistant?.replyStatus === 'Processing'
  }

  /**
   * Gets the list of names that are currently typing.
   */
  @computed get typingNames() {
    return this.isAssistantTyping ? ['Tax Assistant'] : []
  }

  /**
   * Gets a list of events that appear in the conversation timeline.
   */
  @computed get conversationEvents() {
    // Wait until we've loaded the first batch of messages before
    // returning any events.
    if (this.conversationVm.fetchMessages.pending) {
      return []
    }

    return this.assistantLeft ? [this.assistantLeft] : []
  }

  /**
   * Whether the assistant has left the chat, and if so,
   * at what time.
   */
  @computed get assistantLeft(): TaxAssistantLeft | null {
    if (!this.taxAssistant || this.taxAssistant.active) {
      return null
    }

    return {
      type: 'TaxAssistantLeft',
      date: this.taxAssistant.lastUpdated,
    }
  }

  /**
   * Suggestions to display.
   */
  get suggestions(): readonly string[] {
    if (this.taxAssistant?.active) {
      return taxAssistantMessageSuggestions
    }
    return []
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /**
   * Fetches data for the conversation.
   */
  fetch = task(async (signal: AbortSignal) => {
    signal.throwIfAborted()

    const [progression] = await Promise.all([
      this._progressionStore.fetchProgression(this.job.id),
      this._taxAssistantStore.fetch(this.job.id),
      this.conversationVm.fetchMessages(signal),
    ])
    runInAction(() => {
      this.progression = progression
    })
  })

  /**
   * Fetches the unread count.
   */
  async fetchUnreadCount(signal?: AbortSignal) {
    // The conversation will contain the unread count.
    await this.conversationVm.fetchConversation(signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Creates a wrapper message view model for the inner one.
   * This lets us match on special messages such as progression updates.
   *
   * @param messageVm
   * @private
   */
  private _createJobMessageViewModel(
    messageVm: ConversationMessageViewModel,
  ): JobMessageViewModel {
    if (messageVm.message.content.type === 'ExternalReference') {
      switch (messageVm.message.content.externalType) {
        case WellKnownExternalReferenceTypes.TaxAssistant:
          return new TaxAssistantMessageViewModel(messageVm)
        case WellKnownExternalReferenceTypes.Progression:
          return new TaxProgressionMessageViewModel(
            () => this.progression,
            this._viewLatestDraft,
            this.job,
            messageVm,
          )
      }
    }

    return new DefaultJobMessageViewModel(messageVm)
  }
}

/**
 * Well-known external reference types for external reference content.
 */
export const WellKnownExternalReferenceTypes = {
  TaxAssistant: 'worklayer.jobs.assistant.OpenAI',
  Progression: 'worklayer.jobs.Progression',
} as const

/**
 * Tax-specific events that appear in the conversation timeline.
 */
export type TaxJobConversationEvent = TaxAssistantLeft

/**
 * The tax assistant left the chat.
 */
export interface TaxAssistantLeft {
  type: 'TaxAssistantLeft'
  date: Date
}

export type JobMessageViewModel =
  | DefaultJobMessageViewModel
  | TaxProgressionMessageViewModel
  | TaxAssistantMessageViewModel

/**
 * Base class for job message view models. Acts as an interface with default
 * implementations.
 */
export abstract class JobMessageViewModelBase implements ActivationLifecycle {
  protected constructor(
    public readonly messageVm: ConversationMessageViewModel,
  ) {}

  /**
   * @inheritDoc
   * @virtual
   **/
  activate() {
    /* no-op; can be overridden */
  }

  /**
   * @inheritDoc
   * @virtual
   **/
  deactivate() {
    /* no-op; can be overridden */
  }
}

/**
 * Simple wrapper around a regular message's view model.
 * Also acts as a base class.
 */
export class DefaultJobMessageViewModel extends JobMessageViewModelBase {
  constructor(messageVm: ConversationMessageViewModel) {
    super(messageVm)
  }
}

/**
 * A message view model for a job progression step.
 */
export class TaxProgressionMessageViewModel extends JobMessageViewModelBase {
  constructor(
    private readonly _getProgression: () => JobProgression | null,
    private readonly _viewLatestDraft: () => unknown,
    private readonly _job: SubmittedJob,
    messageVm: ConversationMessageViewModel,
  ) {
    super(messageVm)
  }

  /**
   * The current progression.
   */
  get progression() {
    return this._getProgression()
  }

  /**
   * Gets the step + content for the associated progression step.
   */
  @computed get step() {
    const externalContent = this.messageVm.message
      .content as ExternalReferenceContent

    if (!this.progression || !(this._job.type instanceof TaxJobType)) {
      return null
    }

    const computedFlow = computeProgressionFlow({
      flow: this.progression.flow,
      steps: this.progression.recordedSteps.items.map((s) => s.step),
      isExtensionOnlyJob: this._job.type.isExtensionOnlyJob,
      ignoreVisibility: true,
    })

    const recordedStep = this.progression.recordedSteps.find(
      (s) => s.id === externalContent.externalId,
    )

    const computedStep = computedFlow.find((s) => s.step === recordedStep?.step)
    if (!computedStep || !recordedStep) {
      // This can happen when the progression is swapped during the extension
      // flow. There currently isn't a way to mitigate this, so we'll
      // show some generic content for now.
      return {
        step: 'Other',
        content: {
          title: 'Job update',
          description:
            'There was an update on the job, but the details are currently unavailable',
        } as const,
      } as const
    }

    const completedAt = recordedStep.response?.respondedAt

    // We only support a handful of steps.
    switch (computedStep.step) {
      case 'DraftInReview': {
        return {
          step: 'DraftInReview',
          completedAt,
          content: computedStep.content,
        } as const
      }
      case 'DraftRejected': {
        return {
          step: 'DraftRejected',
          completedAt,
          content: computedStep.content,
        } as const
      }
      case 'DraftApproved': {
        return {
          step: 'DraftApproved',
          completedAt,
          content: computedStep.content,
        } as const
      }
      // By default, we'll fall back to whatever the content says.
      default: {
        return {
          step: 'Other',
          content: computedStep.content,
        } as const
      }
    }
  }

  /**
   * Shows the latest draft.
   *
   */
  @action.bound
  viewLatestDraft() {
    this._viewLatestDraft()
  }
}

/**
 * A message view model for a message sent by the tax assistant.
 */
export class TaxAssistantMessageViewModel extends JobMessageViewModelBase {
  constructor(messageVm: ConversationMessageViewModel) {
    super(messageVm)
  }

  /**
   * The tax assistant's external reply ID.
   * This is the ID of the message on the LLM side.
   */
  @computed get externalReplyId() {
    // We know this is safe.
    return (this.messageVm.message.content as ExternalReferenceContent)
      .externalId
  }

  /** @inheritDoc **/
  activate() {
    /**/
  }

  /** @inheritDoc **/
  deactivate() {
    /**/
  }
}

/**
 * Suggestions for messages for the user to send to the tax assistant.
 */
export const taxAssistantMessageSuggestions = [
  `How do I know if I should itemize deductions or take the standard deduction?`,
  `What documents do I need for capital gains or losses?`,
  `What documents are needed to claim dependents?`,
  `What qualifies as foreign income for the Foreign Tax Credit (FTC)?`,
  `What documents do I need to deduct mortgage interest?`,
  `What kind of proof do I need for charitable donations?`,
  `What medical expenses are deductible?`,
  `What if I have multiple income sources (e.g., freelancing, investments, rental properties)?`,
  `What deadlines should I keep in mind for document gathering?`,
  `What records should I have for business income and expenses?`,
]
